import { IconeFechar, IconeHistorico } from '../../components/Icones'
import { cn } from '../../lib/cn'
import { data, dataHoraPorExtenso } from '../../lib/datas'
import { ROTULO_ORIGEM_CHAMADO, TEXTOS } from '../../lib/glossario'
import { usePreferenciaLocal } from '../../lib/preferencias'
import type { OrigemDoChamado } from '../../types/chamado'

interface Props {
  /** As procedências que produziram o que está na tela. Vazio quando não há dado nenhum. */
  origens: OrigemDoChamado[]
  /** A data de corte: o dado mais recente que a tela está exibindo. */
  coletaMaisRecente: string | null
  /** Uma segunda linha, quando a tela sabe de algo mais grave — cobertura incompleta, por exemplo. */
  ressalva?: string
  /**
   * Nome da preferência que guarda o estado recolhido. Sem ele, o carimbo não recolhe.
   *
   * Cada tela escolhe o seu, para o fiscal recolher o da listagem sem recolher o do fechamento —
   * são duas leituras diferentes, e a do fechamento é a que vira documento assinado.
   */
  preferencia?: string
}

const BOTAO =
  'shrink-0 rounded-lg p-1 text-laranja-700 transition-colors hover:bg-laranja-100 ' +
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-laranja-700'

/**
 * O carimbo de procedência: de onde vieram estes números e até quando eles vão.
 *
 * **Permanente, e não um aviso que aparece só quando algo está errado.** Nada está errado: o dado é
 * o que existe. O que seria errado é apresentá-lo sem a data de corte. A base do e-Niterói
 * alcançável está congelada em 23/05/2026 e a planilha de maio cobre 64 dos 91 chamados que a
 * fiscalização considerou; uma tela que mostra 64 linhas sem dizer até onde o dado vai deixa
 * qualquer leitor concluir que houve 64 chamados no contrato inteiro. O número sai com aparência de
 * certo, e é sobre número com aparência de certo que se assina glosa.
 *
 * O ícone é o do histórico, o mesmo que marca procedência na GMUD, e por isso mesmo: os dois dizem
 * "isto tem data e a data importa".
 *
 * Cada tela informa o que ela de fato sabe. A listagem passa as procedências e a coleta mais
 * recente dos chamados que está mostrando; o fechamento passa a cobertura apurada da competência,
 * que é global daquele mês. Um carimbo que afirmasse mais do que a tela mediu seria o próprio
 * defeito que ele existe para evitar.
 *
 * **Recolher não é fechar.** O X encolhe a faixa para uma linha, e essa linha continua trazendo a
 * fonte e a data de corte — as duas coisas que mudam a leitura do número. Sumir de vez com o
 * carimbo devolveria exatamente o defeito que ele existe para evitar: uma tabela de 444 linhas sem
 * dizer até quando o dado vai se lê como "o contrato teve 444 chamados". O que atrapalha é o
 * tamanho do bloco, e é o tamanho que o X resolve.
 *
 * @param props procedências, data de corte, ressalva e a preferência que guarda o recolhimento
 * @returns a faixa de procedência
 */
export function CarimboDeProcedencia({ origens, coletaMaisRecente, ressalva, preferencia }: Props) {
  const { rotulo, fonte, ateAqui, alcance, semDado, comoAtualizar, recolher, expandir, resumo } =
    TEXTOS.procedenciaDoDado
  const nomes = origens.map((origem) => ROTULO_ORIGEM_CHAMADO[origem] ?? origem)
  const [recolhido, definirRecolhido] = usePreferenciaLocal(`carimbo:${preferencia ?? ''}`, false)

  if (preferencia && recolhido) {
    return (
      <section
        aria-label={rotulo}
        className={cn(
          'flex items-center gap-2 rounded-xl border border-laranja-200 bg-laranja-50/60 px-3 py-1.5',
          'text-xs text-slate-700',
        )}
      >
        <IconeHistorico className="h-4 w-4 shrink-0 text-laranja-700" />
        <p className="min-w-0 flex-1 truncate">
          {nomes.length > 0 ? nomes.join(' e ') : semDado}
          {coletaMaisRecente && (
            <>
              {' · '}
              {resumo}{' '}
              <time dateTime={coletaMaisRecente} title={dataHoraPorExtenso(coletaMaisRecente)} className="font-medium">
                {data(coletaMaisRecente)}
              </time>
            </>
          )}
          {ressalva && <span className="font-medium text-laranja-900"> · {ressalva}</span>}
        </p>
        <button type="button" onClick={() => definirRecolhido(false)} className={BOTAO} aria-label={expandir}>
          <span aria-hidden className="px-1 text-sm leading-none">
            +
          </span>
        </button>
      </section>
    )
  }

  return (
    <section
      aria-label={rotulo}
      className="flex gap-3 rounded-xl border border-laranja-300 bg-laranja-50 px-4 py-3 text-sm text-slate-800"
    >
      <IconeHistorico className="mt-0.5 h-5 w-5 shrink-0 text-laranja-700" />
      <div className="min-w-0 flex-1 space-y-1">
        <p className="max-w-prose">
          <strong className="font-semibold">{rotulo}.</strong>{' '}
          {nomes.length > 0 ? (
            <>
              {fonte} <span className="font-medium">{nomes.join(' e ')}</span>.{' '}
            </>
          ) : (
            <>{semDado} </>
          )}
          {coletaMaisRecente && (
            <>
              {ateAqui}{' '}
              {/* `<time>` com a data por extenso no `title`: a coluna mostra 23/05/2026 e quem
                  precisa da hora exata — que é o que decide a borda de um prazo — a tem ao lado. */}
              <time dateTime={coletaMaisRecente} title={dataHoraPorExtenso(coletaMaisRecente)} className="font-medium">
                {data(coletaMaisRecente)}
              </time>
              . {alcance}
            </>
          )}
        </p>
        {ressalva && <p className="max-w-prose font-medium text-laranja-900">{ressalva}</p>}
        {!coletaMaisRecente && <p className="max-w-prose">{comoAtualizar}</p>}
      </div>
      {preferencia && (
        <button
          type="button"
          onClick={() => definirRecolhido(true)}
          className={cn(BOTAO, 'mt-0.5 self-start')}
          aria-label={recolher}
        >
          <IconeFechar className="h-4 w-4" />
        </button>
      )}
    </section>
  )
}
